import React, { useState, useEffect } from 'react';
import { 
    Button, 
    Typography, 
    Box, 
    Card, 
    CardContent, 
    Divider, 
    Switch, 
    List, 
    ListItem, 
    CircularProgress, 
    Grid,
    Container,
    Stack,
    useMediaQuery
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import SettingsIcon from '@mui/icons-material/Settings';
import { useGoogleCalendarContext } from '../../../context/google_calendar/GoogleCalendarContext';
import { useTheme as appTheme } from '../../../context/theme_context/ThemeContext';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { useUserContext } from '../../../context/user_context/UserContext';
import { styled } from '@mui/material/styles';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';

const DarkLightSwitch = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 34,
    padding: 7,
    '& .MuiSwitch-switchBase': {
        margin: 1,
        padding: 0,
        transform: 'translateX(6px)',
        '&.Mui-checked': {
            color: '#fff',
            transform: 'translateX(22px)',
            '& .MuiSwitch-thumb:before': {
                backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
                    '#fff',
                )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
            },
            '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: '#aab4be',
                ...theme.applyStyles('dark', {
                    backgroundColor: '#8796A5',
                }),
            },
        },
    },
    '& .MuiSwitch-thumb': {
        backgroundColor: '#4287f5',
        width: 32,
        height: 32,
        '&::before': {
            content: "''",
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
                '#fff',
            )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
        },
        ...theme.applyStyles('dark', {
            backgroundColor: '#003892',
        }),
    },
    '& .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#98B0D8',
        borderRadius: 20 / 2,
        ...theme.applyStyles('dark', {
            backgroundColor: '#8796A5',
        }),
    },
}));

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
    },
}));

const formatLastSynced = (isoTimestamp) => {
    if (!isoTimestamp) return '';
    const date = new Date(isoTimestamp);
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};

const Settings = () => {
    const { t } = useTranslation();
    const { 
        isGoogleConnected, 
        handleGoogleSignIn, 
        handleGoogleDisconnect, 
        lastSyncTime, 
        isLoading 
    } = useGoogleCalendarContext();
    const { firebaseUser } = useUserContext()
    const { themeMode, toggleTheme } = appTheme();
    
    // Use MUI's useMediaQuery hook to detect mobile devices
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleThemeToggle = () => {
        toggleTheme(firebaseUser.uid);
    };

    return (
        <AppSidebar
            content={
                <>
                    <Box sx={{ 
                        p: 3
                    }}>
                        <Typography variant="h5" component="h5">
                            {t(StringKeys.SETTINGS)}
                        </Typography>
                    </Box>

                    <Box sx={{ px: 3 }}>
                        <Grid container spacing={3}>
                            {!isMobile && (
                                <Grid item xs={12} md={6}>
                                    <StyledCard>
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography variant="h6" gutterBottom>
                                                {t(StringKeys.INTEGRATIONS)}
                                            </Typography>
                                            <Divider sx={{ mb: 3 }} />

                                            <List sx={{ p: 0 }}>
                                                <ListItem
                                                    sx={{
                                                        px: 0,
                                                        flexDirection: { xs: 'column', sm: 'row' },
                                                        alignItems: { xs: 'stretch', sm: 'center' },
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1, mb: { xs: 2, sm: 0 } }}>
                                                        <Typography variant="subtitle1" fontWeight="medium">
                                                            {t(StringKeys.GOOGLE_CALENDAR)}
                                                        </Typography>
                                                        {!isGoogleConnected && (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                {t(StringKeys.CONNECT_TO_SYNC_YOUR_EVENTS_WITH_GOOGLE_CALENDAR)}
                                                            </Typography>
                                                        )}
                                                        {isGoogleConnected && lastSyncTime && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                                {t(StringKeys.LAST_SYNCED_, {time: formatLastSynced(lastSyncTime)})}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        justifyContent: { xs: 'stretch', sm: 'flex-end' },
                                                        width: { xs: '100%', sm: 'auto' }
                                                    }}>
                                                        {isLoading ? (
                                                            <CircularProgress size={24} />
                                                        ) : isGoogleConnected ? (
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                onClick={handleGoogleDisconnect}
                                                                fullWidth={false}
                                                                sx={{ minWidth: 120 }}
                                                            >
                                                                {t(StringKeys.DISCONNECT)}
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="contained"
                                                                startIcon={<GoogleIcon />}
                                                                onClick={handleGoogleSignIn}
                                                                fullWidth={false}
                                                                sx={{
                                                                    minWidth: 120,
                                                                    backgroundColor: '#4285F4',
                                                                    '&:hover': {
                                                                        backgroundColor: '#3367D6',
                                                                    },
                                                                }}
                                                            >
                                                                {t(StringKeys.CONNECT)}
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </StyledCard>
                                </Grid>
                            )}
                            <Grid item xs={12} md={isMobile ? 12 : 6}>
                                <StyledCard>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t(StringKeys.THEME)}
                                        </Typography>
                                        <Divider sx={{ mb: 3 }} />
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            px: 1 
                                        }}>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight="medium">
                                                    {t(StringKeys.APPEARANCE)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {themeMode === 'dark' ? t(StringKeys.DARK_MODE) : t(StringKeys.LIGHT_MODE)}
                                                </Typography>
                                            </Box>
                                            <DarkLightSwitch
                                                checked={themeMode === 'dark'}
                                                onChange={handleThemeToggle}
                                                inputProps={{ 'aria-label': 'theme toggle' }}
                                            />
                                        </Box>
                                    </CardContent>
                                </StyledCard>
                            </Grid>
                        </Grid>
                    </Box>
                </>
            }
        />
    );
};

export default Settings;